<template>
    <div
        :key="componentUpdateIndex"
        :class="{ 'content-block': !userCustomComponents }"
    >
        <b-form name="pageConfigForm" v-on:submit.prevent="$_onSaveSettings">
            <div class="form-group">
                <a
                    href="#"
                    v-if="customComponents"
                    @click.stop="$_resetActivatedComponents()"
                    >{{ $t('printComponent.dashboard.resetComponents') }}</a
                >
            </div>
            <div
                v-for="(componentSorted, index) in sortedUserDefaultComponents"
                :key="`${
                    userCustomComponents ? 'custom' : 'default'
                }_component_${componentSorted.name}`"
                class="accordion"
                role="tablist"
            >
                <b-card no-body class="mb-1">
                    <b-card-header
                        @click="$_selectComponent(index)"
                        :class="{
                            'p-1': true,
                            collapsed: !accordionState[index],
                            'not-collapsed': accordionState[index]
                        }"
                        header-tag="header"
                        role="tab"
                        data-toggle="collapse"
                    >
                        <span>{{ componentSorted.text }}</span>
                        <div class="float-right">
                            <a
                                v-if="!componentSorted.required"
                                :title="
                                    $t(
                                        'printComponent.dashboard.deletecomponent'
                                    )
                                "
                                class="pr-4"
                                @click.stop.prevent="
                                    $_activateComponent(
                                        componentSorted.name,
                                        false
                                    )
                                "
                            >
                                <em class="fas fa-trash-alt fa-sm"></em>
                            </a>
                            <em class="fa iconCollapseExpand"></em>
                        </div>
                    </b-card-header>

                    <b-collapse
                        accordion="component-accordion"
                        v-model="accordionState[index]"
                        role="tabpanel"
                    >
                        <page-component
                            v-if="accordionState[index]"
                            :component-configuration="componentSorted.component"
                            :component-name="componentSorted.name"
                            :print-configuration="printConfiguration"
                            :custom-components="customComponents"
                            :mode="mode"
                            @changeDefaultSettings="
                                $_onChangeDefaultSettings(
                                    $event,
                                    componentSorted.name
                                )
                            "
                            @saveSettings="
                                $_onChangeSettings($event, componentSorted.name)
                            "
                        />
                    </b-collapse>
                </b-card>
            </div>
            <div class="pt-2">
                <b-button
                    v-if="sortedUnusedComponents.length > 0"
                    @click="$_toggleAddComponent"
                    >{{ $t('btnAddLabel') }}</b-button
                >
                <b-container v-if="addComponent" class="text-center">
                    <b-row>
                        <b-col
                            cols="4"
                            class="p-3"
                            v-for="unusedComponent in sortedUnusedComponents"
                            :key="`${
                                userCustomComponents ? 'custom' : 'default'
                            }_unused_component_${unusedComponent.name}`"
                        >
                            <b-button
                                type="button"
                                @click="
                                    $_activateComponent(
                                        unusedComponent.name,
                                        true
                                    )
                                "
                                >{{ unusedComponent.text }}</b-button
                            >
                        </b-col>
                    </b-row>
                </b-container>
            </div>
            <!-- in context of custom setting of a paperformat, saving is handled by paperChoices.vue -->
            <div
                v-if="!userCustomComponents"
                class="sticky-bottom fixed-bottom"
            >
                <submit-cancel-remove
                    :id="`pageConfig`"
                    :is-saving="isSaving"
                    :mode="modeAddConst"
                    @cancel="$_onCancel"
                >
                </submit-cancel-remove>
            </div>
        </b-form>
    </div>
</template>

<script>
    import { MODE_ADD } from '../../../constants';

    import PageComponent from './PageComponent.vue';
    import SubmitCancelRemove from '../../../components/SubmitCancelRemove.vue';
    import modalMixin from '../../../components/Mixins/ModalMixin.vue';

    export default {
        mixins: [modalMixin],
        components: {
            'page-component': PageComponent,
            'submit-cancel-remove': SubmitCancelRemove
        },
        props: {
            customComponents: { type: Object, required: false },
            defaultComponents: { type: Object, required: true },
            printConfiguration: { type: Object, required: true },
            mode: { type: String, required: false, default: 'shared' },
            isSaving: { type: Boolean, required: false, default: false }
        },
        data() {
            return {
                // Constants
                modeAddConst: MODE_ADD,

                userDefaultComponents: null,
                userCustomComponents: null,
                componentUpdateIndex: 0,
                accordionState: Object.keys(this.defaultComponents).map(
                    () => false
                ),
                addComponent: false
            };
        },
        watch: {
            defaultComponents: {
                handler: 'initDefaultComponents',
                immediate: true
            },
            customComponents: {
                handler: 'initCustomComponents',
                immediate: true
            }
        },
        methods: {
            init() {
                this.initDefaultComponents();
                this.initCustomComponents();
            },
            $_onCancel() {
                this.$router.push({ path: '/' });
            },
            initDefaultComponents() {
                this.userDefaultComponents = JSON.parse(
                    JSON.stringify(this.defaultComponents)
                );
            },
            initCustomComponents() {
                this.userCustomComponents =
                    this.customComponents &&
                    JSON.parse(JSON.stringify(this.customComponents));
            },
            $_toggleAddComponent() {
                this.addComponent = !this.addComponent;
            },
            async $_activateComponent(componentName, activated) {
                if (activated === false) {
                    if (
                        (await this.msgBoxConfirm({
                            message: this.$t(
                                'printComponent.dashboard.confirmRemoveComponent'
                            )
                        })) === false
                    ) {
                        return undefined;
                    }
                }
                await this.$_askToSaveChanges();
                const defaultOptions =
                    this.printConfiguration?.components?.configuration?.[
                        componentName
                    ];
                if (this.customComponents) {
                    if (!this.userCustomComponents[componentName]) {
                        this.userCustomComponents[componentName] = {};
                    }
                    const component = this.userCustomComponents[componentName];
                    component.options = {
                        ...defaultOptions,
                        ...component.options,
                        required: activated
                    };
                    this.userCustomComponents = { ...this.userCustomComponents };
                } else {
                    const component = this.userDefaultComponents[componentName];
                    component.options = {
                        ...defaultOptions,
                        ...component.options,
                        required: activated
                    };
                    this.userDefaultComponents = { ...this.userDefaultComponents };
                }
                return this.$_onSaveSettings();
            },
            async $_resetActivatedComponents() {
                await this.$_askToSaveChanges();
                if (this.customComponents) {
                    this.userCustomComponents = this.userDefaultComponents;
                    this.$_onSaveSettings();
                }
            },
            $_onChangeDefaultSettings(defaultSettings, componentName) {
                const newSettings = { ...this.userDefaultComponents };
                newSettings[componentName] = defaultSettings;
                this.userDefaultComponents = newSettings;
            },
            $_onChangeSettings(settings, componentName) {
                const newSettings = { ...this.userCustomComponents };
                newSettings[componentName] = settings[componentName];
                this.userCustomComponents = newSettings;
                this.$emit('update', this.userCustomComponents);
            },
            $_onSaveSettings() {
                if (this.customComponents) {
                    this.$emit('save', this.userCustomComponents);
                } else {
                    this.$emit('save', this.userDefaultComponents);
                }
            },
            async $_selectComponent(index) {
                await this.$_askToSaveChanges();
                const newVal = !this.accordionState[index];
                this.accordionState = this.accordionState.map(() => false);
                this.accordionState[index] = newVal;
                this.componentUpdateIndex += 1;
            },
            async $_askToSaveChanges() {
                if (this.dirty) {
                    const firstSave = await this.msgBoxConfirm({
                        message: this.$t(
                            'printComponent.dashboard.confirmSaveFormat'
                        )
                    });
                    if (firstSave) {
                        this.$_onSaveSettings();
                    } else {
                        this.init();
                    }
                }
            },
            $_arrayTextSort(a, b) {
                if (a.text < b.text) {
                    return -1;
                }
                if (a.text > b.text) {
                    return 1;
                }
                return 0;
            },
            $_isComponentAddedToTemplate(componentName) {
                return (
                    // if the default value of required is true, the component is allways added (chart)
                    this.printConfiguration?.components?.configuration?.[
                        componentName
                    ].required === true ||
                    // if there is no value in CustomComponents (in a template), use the value of DefaultComponents
                    (typeof this.userCustomComponents?.[componentName]?.options
                        ?.required !== 'boolean' &&
                        this.userDefaultComponents[componentName]?.options
                            ?.required === true) ||
                    // otherwise return the value in the CustomComponents (in a template)
                    this.userCustomComponents?.[componentName]?.options
                        ?.required === true
                );
            }
        },
        computed: {
            dirtyCustom: function () {
                return (
                    JSON.stringify(this.customComponents) !==
                    JSON.stringify(this.userCustomComponents)
                );
            },
            dirtyDefault: function () {
                return (
                    JSON.stringify(this.defaultComponents) !==
                    JSON.stringify(this.userDefaultComponents)
                );
            },
            dirty: function () {
                return this.dirtyDefault || this.dirtyCustom;
            },
            sortedUnusedComponents: function () {
                return Object.keys(this.userDefaultComponents)
                    .filter((componentName) => {
                        return !this.$_isComponentAddedToTemplate(componentName);
                    })
                    .map((componentName) => {
                        return {
                            name: componentName,
                            text: this.$t(
                                `printComponent.${componentName}`
                            ).toUpperCase()
                        };
                    })
                    .sort(this.$_arrayTextSort);
            },
            sortedUserDefaultComponents: function () {
                return Object.keys(this.userDefaultComponents)
                    .filter((componentName) => {
                        return this.$_isComponentAddedToTemplate(componentName);
                    })
                    .map((componentName) => {
                        const factoryDefaults =
                            this.printConfiguration?.components?.configuration?.[
                                componentName
                            ];
                        return {
                            name: componentName,
                            required: factoryDefaults?.required,
                            optional: factoryDefaults?.optional === true,
                            initial: factoryDefaults?.initial === true,
                            text: this.$t(
                                `printComponent.${componentName}`
                            ).toUpperCase(),
                            component: this.userDefaultComponents[componentName]
                        };
                    })
                    .sort(this.$_arrayTextSort);
            }
        }
    };
</script>

<style scoped>
.content-block {
    margin-bottom: 100px;
}
</style>
